import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, ModalProps, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { AiOutlineCaretRight } from 'react-icons/ai';
import { CustomFileMap, FileNode, SelectedFolder } from '../../../types/FileManagerTypes';
import FileManagerService from '../../../services/FileManager/fileManager.service';
import { ROOT_DISPLAY_NAME } from '../../../views/FileManager/constants';
import ConditionalRender from '../ConditionalRender';
import styles from './folderSelectorTree.module.scss';

export interface FolderSelectorTreeProps extends Omit<ModalProps, 'onCancel' | 'children' | 'footer' | 'title'> {
  open: boolean;
  onClose: () => void;
  /**
   * Called when the user confirms a folder. Return `false` (sync or async) to
   * keep the modal open — used for client-side validation failures.
   */
  onSelect: (folder: SelectedFolder) => void | boolean | Promise<void | boolean>;
  /** Visual theme. Defaults to dark (File Manager). */
  variant?: 'light' | 'dark';
  /**
   * Optional modal header title (rendered uppercase).
   * When omitted, the header is not rendered (headerless).
   */
  title?: string;
  /**
   * Optional descriptive label above the tree box (rendered uppercase).
   * When omitted, the label is not rendered.
   */
  bodyLabel?: string;
  /** Label for the root shortcut (rendered uppercase). */
  rootLabel?: string;
  /** Secondary footer button label (rendered uppercase). Defaults to "BACK". */
  secondaryLabel?: string;
  /** Primary footer button label (rendered uppercase). Defaults to "ADD". */
  primaryLabel?: string;
  /**
   * External folder/file map. When provided, skips the internal fetch and is
   * the sole data source (callers should pass home getfilemapping). Non-dir
   * nodes are ignored when rendering.
   */
  folderMap?: CustomFileMap;
  /**
   * Root id for the external map (and preferred root when self-fetching).
   * Required for correct tree walking when `folderMap` is passed.
   */
  rootFolderId?: string;
  /** Pre-selects this folder and expands its ancestors every time the modal opens. */
  initialFolderId?: string;
}

const byName = (a: FileNode | null, b: FileNode | null) => (a?.name ?? '').localeCompare(b?.name ?? '');

const FolderSelectorTree: React.FC<FolderSelectorTreeProps> = ({
  open,
  onClose,
  onSelect,
  variant = 'dark',
  title,
  bodyLabel,
  rootLabel = ROOT_DISPLAY_NAME,
  secondaryLabel = 'BACK',
  primaryLabel = 'ADD',
  folderMap: folderMapProp,
  rootFolderId: rootFolderIdProp,
  initialFolderId,
  ...modalProps
}) => {
  const [selectedId, setSelectedId] = useState<string>('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const treeRef = useRef<HTMLDivElement | null>(null);
  /** One-shot auto-scroll per open; reset on close. */
  const didInitialScrollRef = useRef(false);
  /** Ignore scroll events fired by our own programmatic scroll. */
  const ignoreScrollEventRef = useRef(false);

  const hasExternalMap = folderMapProp !== undefined;

  const { getFileMapping } = FileManagerService();
  // staleTime 0 (against the app-wide 30s default): a destination picker must
  // list folders created since the last open.
  const { data, isFetching } = useQuery({
    ...getFileMapping(open && !hasExternalMap),
    staleTime: 0,
  });

  // External map is authoritative when provided — never mix with query cache.
  const fileMap = hasExternalMap ? (folderMapProp ?? {}) : (data?.fileMap ?? {});
  const rootId = rootFolderIdProp || data?.rootFolderId || '';

  const showLoader = hasExternalMap ? false : isFetching;

  const childFolders = useMemo(
    () => (id: string) =>
      (fileMap[id]?.childrenIds ?? [])
        .map((cid) => fileMap[cid])
        .filter((f): f is FileNode => Boolean(f?.isDir))
        .sort(byName),
    [fileMap],
  );

  // This component stays mounted across open/close (only the Modal's own DOM
  // is torn down via destroyOnClose — see handleCancel), so selection and
  // expand state must be re-synced to `initialFolderId` on every open.
  useEffect(() => {
    if (!open) return;

    if (!initialFolderId || initialFolderId === rootId || !fileMap[initialFolderId]) {
      setSelectedId(rootId);
      setExpandedIds(new Set());
      return;
    }

    const chain: string[] = [];
    let cursor: string | undefined = initialFolderId;
    const seen = new Set<string>();
    while (cursor && cursor !== rootId && !seen.has(cursor)) {
      seen.add(cursor);
      chain.unshift(cursor);
      cursor = fileMap[cursor]?.parentId;
    }

    // Expand every ancestor so the pre-selected node is visible; do not
    // auto-expand the selected node itself.
    setExpandedIds(new Set(chain.slice(0, -1)));
    setSelectedId(initialFolderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFolderId, rootId, fileMap]);

  // Reset one-shot scroll when the modal closes so the next open can refocus.
  useEffect(() => {
    if (!open) {
      didInitialScrollRef.current = false;
      ignoreScrollEventRef.current = false;
    }
  }, [open]);

  // User scroll cancels any pending programmatic focus for this open.
  useEffect(() => {
    const el = treeRef.current;
    if (!open || !el || showLoader) return;
    const onScroll = () => {
      if (ignoreScrollEventRef.current) return;
      didInitialScrollRef.current = true;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [open, showLoader]);

  const topLevelFolders = childFolders(rootId);
  const effectiveSelectedId = selectedId || rootId;

  /**
   * Scroll the preselected row into the tree viewport (both axes).
   *
   * Important: selection is applied in a useEffect (after paint). A layout
   * effect that runs on the same open tick still sees selectedId="" and would
   * wrongly treat the target as root and mark scroll done. Wait until
   * selectedId has been set for this open, then scroll once the row exists.
   *
   * Manual offsets against `.tree` avoid scrollIntoView scrolling the modal
   * or page ancestors.
   */
  useEffect(() => {
    if (!open || didInitialScrollRef.current || showLoader) return;
    // Selection sync has not run yet — do not treat "" as root.
    if (!selectedId) return;

    if (selectedId === rootId) {
      didInitialScrollRef.current = true;
      return;
    }

    let cancelled = false;
    let raf2 = 0;

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (cancelled || didInitialScrollRef.current) return;

        const treeEl = treeRef.current;
        if (!treeEl) return;

        const row = treeEl.querySelector(`[data-folder-id="${CSS.escape(selectedId)}"]`) as HTMLElement | null;
        // Ancestors may not be expanded/painted yet — retry on next dep change.
        if (!row) return;

        const treeRect = treeEl.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();

        let nextTop = treeEl.scrollTop;
        let nextLeft = treeEl.scrollLeft;

        // Vertical: prefer centering when the row is outside the viewport.
        if (rowRect.top < treeRect.top || rowRect.bottom > treeRect.bottom) {
          nextTop += rowRect.top - treeRect.top - treeRect.height / 2 + rowRect.height / 2;
        }

        // Horizontal: deep indent can push the name past the right edge.
        if (rowRect.left < treeRect.left || rowRect.right > treeRect.right) {
          nextLeft += rowRect.left - treeRect.left - treeRect.width / 2 + rowRect.width / 2;
        }

        nextTop = Math.max(0, nextTop);
        nextLeft = Math.max(0, nextLeft);

        if (nextTop !== treeEl.scrollTop || nextLeft !== treeEl.scrollLeft) {
          ignoreScrollEventRef.current = true;
          treeEl.scrollTo({ top: nextTop, left: nextLeft });
          // Release after the scroll event has had a chance to fire.
          requestAnimationFrame(() => {
            ignoreScrollEventRef.current = false;
          });
        }

        didInitialScrollRef.current = true;
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [open, selectedId, expandedIds, fileMap, showLoader, rootId]);

  const buildAncestorChain = (folderId: string): string[] => {
    const chain: string[] = [];
    let cursor: string | undefined = folderId;
    const seen = new Set<string>();
    while (cursor && cursor !== rootId && !seen.has(cursor)) {
      seen.add(cursor);
      chain.unshift(cursor);
      cursor = fileMap[cursor]?.parentId;
    }
    return chain;
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelect = async () => {
    const id = effectiveSelectedId;
    const folder: SelectedFolder =
      id === rootId
        ? { id: rootId, name: ROOT_DISPLAY_NAME, s3Key: rootId }
        : {
            id,
            name: fileMap[id]?.name ?? ROOT_DISPLAY_NAME,
            s3Key: [rootId, ...buildAncestorChain(id)].join('/'),
          };

    const result = await onSelect(folder);
    if (result === false) return;
    onClose();
  };

  // Discards any un-confirmed navigation. Selection/expand state lives on this
  // component, which stays mounted across open/close, so it must be reset on
  // every "leave without selecting" path.
  const handleCancel = () => {
    setSelectedId('');
    setExpandedIds(new Set());
    onClose();
  };

  const renderTreeNodes = (parentId: string): React.ReactNode =>
    childFolders(parentId).map((folder) => {
      const hasChildren = childFolders(folder.id).length > 0;
      const isExpanded = expandedIds.has(folder.id);
      const isSelected = effectiveSelectedId === folder.id;

      return (
        <React.Fragment key={folder.id}>
          <div
            role="treeitem"
            tabIndex={0}
            data-folder-id={folder.id}
            aria-selected={isSelected}
            aria-expanded={hasChildren ? isExpanded : undefined}
            title={folder.name}
            className={`${styles.row} ${isSelected ? styles.rowSelected : ''}`}
            onClick={() => setSelectedId(folder.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedId(folder.id);
              }
            }}
          >
            <span className={styles.rowName}>{folder.name}</span>
            {hasChildren && (
              <div
                role="button"
                tabIndex={0}
                className={styles.caretBtn}
                aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(folder.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleToggleExpand(folder.id);
                  }
                }}
              >
                <AiOutlineCaretRight className={`${styles.caret} ${isExpanded ? styles.caretExpanded : ''}`} />
              </div>
            )}
          </div>
          {hasChildren && isExpanded && <div className={styles.children}>{renderTreeNodes(folder.id)}</div>}
        </React.Fragment>
      );
    });

  const isRootSelected = effectiveSelectedId === rootId;
  const treeAriaLabel = bodyLabel ?? title ?? 'Folder tree';

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      centered
      footer={null}
      closable={false}
      maskClosable={false}
      rootClassName={styles.folderModalRoot}
      title={null}
      className={styles.modal}
      width={380}
      destroyOnClose
      {...modalProps}
    >
      <div className={`${styles.container} ${styles[variant]}`}>
        {title ? (
          <header className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
          </header>
        ) : null}

        <div className={styles.body}>
          {bodyLabel ? <p className={styles.bodyLabel}>{bodyLabel}</p> : null}

          <ConditionalRender
            visible={!showLoader}
            fallback={
              <div className={styles.loader}>
                <Spin />
              </div>
            }
          >
            <div className={styles.treeBox}>
              <div
                role="button"
                tabIndex={0}
                data-folder-id={rootId}
                className={`${styles.rootBtn} ${isRootSelected ? styles.rootBtnSelected : ''}`}
                onClick={() => setSelectedId(rootId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedId(rootId);
                  }
                }}
              >
                {rootLabel}
              </div>

              <div ref={treeRef} className={styles.tree} role="tree" aria-label={treeAriaLabel}>
                <div className={styles.treeInner}>{topLevelFolders.length > 0 ? renderTreeNodes(rootId) : null}</div>
              </div>
            </div>
          </ConditionalRender>
        </div>

        <div className={styles.footer}>
          <Button className={styles.backBtn} onClick={handleCancel}>
            {secondaryLabel}
          </Button>
          <Button className={styles.addBtn} onClick={handleSelect}>
            {primaryLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FolderSelectorTree;
